'use client'

import React from 'react'
import { Box, Flex } from '@mantine/core'

interface MobileSearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onFocus'> {
    onActivate: () => void
    /**
     * Optional visible content (typically an icon) layered over the input.
     * When set, the input becomes an invisible tap target filling its parent;
     * className/style apply to the wrapper instead of the input.
     */
    children?: React.ReactNode
}

const baseStyles: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
    fontFamily: 'inherit',
    fontSize: 16, // ≥ 16px to prevent iOS auto-zoom on focus
    appearance: 'none',
    WebkitAppearance: 'none',
    boxSizing: 'border-box',
}

const overlayInputStyles: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    color: 'transparent',
    caretColor: 'transparent',
    cursor: 'pointer',
}

/**
 * A real <input> whose focus opens a search drawer.
 *
 * The native focus event from the user's tap is what pops the iOS keyboard —
 * a programmatic focus call (or readOnly input) won't. Once the drawer opens,
 * its autoFocus moves focus to the drawer's input and iOS keeps the keyboard
 * up across the focus transfer.
 *
 * Two visual variants:
 *   - bare:  pass placeholder/className/style; the input renders as the chrome.
 *   - icon:  pass children; the input becomes an invisible overlay and
 *            children render on top (className/style apply to the wrapper).
 */
export function MobileSearchInput({ onActivate, style, className, children, ...rest }: MobileSearchInputProps) {
    const inputProps = {
        type: 'search' as const,
        onFocus: onActivate,
        autoCorrect: 'off',
        autoCapitalize: 'off',
        spellCheck: false,
        ...rest,
    }

    if (children) {
        return (
            <Box pos="relative" className={className} style={style}>
                <input {...inputProps} style={{ ...baseStyles, ...overlayInputStyles }} />
                <Flex
                    pos="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    style={{ pointerEvents: 'none' }}
                >
                    {children}
                </Flex>
            </Box>
        )
    }

    return <input {...inputProps} className={className} style={{ ...baseStyles, ...style }} />
}
